import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '../config/env';

/**
 * Detects when a newer frontend build has been deployed while the current tab
 * is still running an older bundle.
 *
 * Mechanism: the build emits `dist/version.json` (see `versionManifest` in
 * vite.config.ts) carrying the same build identity baked into this running
 * bundle. We poll that file (cache-busted) and on tab focus, and compare the
 * fetched identity to our own. A mismatch means a deploy happened — we surface
 * `updateAvailable: true` so the UI can offer a refresh. We never reload
 * automatically; that's the caller's (user's) decision so unsaved work survives.
 *
 * No-ops in local dev (`env.buildNumber` is null) — there is no version.json to
 * poll and nothing meaningful to compare.
 */

/** Shape of the file written by the versionManifest Vite plugin. */
interface VersionManifest {
  version: string;
  buildNumber: string | null;
  commitId: string | null;
}

/** Default poll interval: 5 minutes. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Don't re-check more than once per 30s, however many focus events fire. */
const MIN_CHECK_GAP_MS = 30 * 1000;

/** Stable identity string for a build — used purely for equality comparison. */
function buildIdentity(m: Pick<VersionManifest, 'version' | 'buildNumber' | 'commitId'>): string {
  return `${m.version}|${m.buildNumber ?? ''}|${m.commitId ?? ''}`;
}

/** This running bundle's identity, baked in at build time via env.ts. */
const RUNNING_IDENTITY = buildIdentity({
  version: env.appVersion,
  buildNumber: env.buildNumber,
  commitId: env.commitId,
});

export interface UseVersionCheckResult {
  /** True once a different build identity has been observed at /version.json. */
  updateAvailable: boolean;
  /** Reload the page onto the new build. Caller wires this to a "Refresh" button. */
  reload: () => void;
}

export function useVersionCheck(): UseVersionCheckResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const lastCheckRef = useRef(0);

  // Only meaningful for real (CI) builds — dev/preview have no buildNumber.
  const enabled = env.buildNumber !== null;

  const check = useCallback(async () => {
    if (!enabled || updateAvailable) return;

    const now = Date.now();
    if (now - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
    lastCheckRef.current = now;

    try {
      // cache: 'no-store' + cache-bust query so we never read a stale CDN copy.
      const res = await fetch(`/version.json?t=${now}`, { cache: 'no-store' });
      if (!res.ok) return;

      const manifest = (await res.json()) as VersionManifest;
      if (buildIdentity(manifest) !== RUNNING_IDENTITY) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network blip / offline / file briefly missing mid-deploy — ignore and
      // retry on the next tick. A failed check must never disrupt the app.
    }
  }, [enabled, updateAvailable]);

  useEffect(() => {
    if (!enabled) return;

    // Check on mount, on a fixed interval, and whenever the tab regains focus.
    void check();
    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);

    const onFocus = () => void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
